import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Markdown from "react-markdown";
import { makeStyles } from "@material-ui/styles";
import { Link } from "@reach/router";
import Typography from "@material-ui/core/Typography";
import { getLinks } from "../util";
import { AccountContext } from "../accountContext";
import Divider from "@material-ui/core/Divider";
import { Button } from "@mantine/core";
import {
  IconCamera,
  IconReceipt,
  IconUserEdit,
  IconLock,
  IconUserPlus,
  IconRefresh,
  IconTicket,
} from "@tabler/icons-react";

let source;

const getIcon = (image, classes) => {
  switch (image) {
    case "photo":
      return <IconCamera className={classes.icon} />;
    case "customer":
      return <IconUserEdit className={classes.icon} />;
    case "password":
      return <IconLock className={classes.icon} />;
    case "ticket":
      return <IconTicket className={classes.icon} />;
    case "managed":
      return <IconUserPlus className={classes.icon} />;
    case "sale":
      return <IconReceipt className={classes.icon} />;
    case "subscription":
      return <IconRefresh className={classes.icon} />;
    default:
      throw new Error(`wrong icon: ${image}`);
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    "@media(max-width: 1024px)": {
      padding: "16px",
    },
  },
  button: {
    marginBottom: "1rem",
  },
  redirectWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: "1rem",
    marginBottom: "1rem",
  },
  info: {},
  divider: {
    margin: "16px 0",
  },
  activePerson: {
    color: theme.palette.primary.main,
  },
  linkContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    gridGap: "16px",
    "@media(max-width: 667px)": {
      gridTemplateColumns: "1fr",
      gridGap: "8px",
    },
  },
  link: { textDecoration: "none" },
  card: {
    color: theme.app.labelColor,
    height: "100%",
    backgroundColor: theme.app.background[1],
    border: `1px solid ${theme.app.borderColor}`,
    borderRadius: "8px",
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cardHeader: {
    padding: "1rem 0 0.35rem 0",
    textAlign: "center",
    fontSize: "1.25rem",
    fontWeight: 500,
    display: "flex",
    justifyContent: "center",
  },
  cardContent: { padding: "16px", fontSize: "1rem", textAlign: "center" },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  icon: {
    width: "50px",
    height: "50px",
    "@media(max-width: 667px)": {
      width: "40px",
      height: "40px",
    },
  },
}));

const StartPage = (props) => {
  const accountContext = useContext(AccountContext);
  const hasSubscription =
    accountContext.state.accountData.member.subscriptionHistory.length > 0;
  const { config, isSelf } = accountContext.state;
  const language = useSelector((state) => state.language);
  const [text, setText] = useState();
  const links = getLinks(isSelf, config, hasSubscription);
  const classes = useStyles();
  useEffect(() => {
    let CancelToken = axios.CancelToken;
    source = CancelToken.source();
    axios
      .get("/ee-static/members/Texts/Account/accountText.md", {
        cancelToken: source.token,
      })
      .then((response) => setText(response.data))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.log(error);
        }
      });
    return () => source.cancel("Startpage Is Unmounting");
  }, []);

  const goBackUrl = localStorage.getItem("goBackUrl");
  const isBooking = goBackUrl && !goBackUrl.includes("ticketshop");
  const isShop = goBackUrl && goBackUrl.includes("ticketshop");

  const redirect = (endpoint) => {
    const link = config?.apiUrl.split("membersapi")[0];
    if (endpoint === "ticketshop") window.location.replace(`${link}ticketshop`);
    if (endpoint === "booking") window.location.replace(`${link}booking`);
  };

  return (
    <div className={classes.container}>
      {goBackUrl && isBooking ? (
        <Button
          variant="contained"
          color="primary"
          className={`primaryButton ${classes.button}`}
          onClick={() => redirect("booking")}
        >
          {language.Back_To_Booking}
        </Button>
      ) : goBackUrl && isShop ? (
        <Button
          className={`primaryButton ${classes.button}`}
          color="primary"
          onClick={() => redirect("ticketshop")}
        >
          {language.Back_To_Shop}
        </Button>
      ) : (
        <div className={classes.redirectWrapper}>
          <Button
            className={`primaryButton ${classes.button}`}
            color="primary"
            onClick={() => redirect("ticketshop")}
          >
            {language.Back_To_Shop}
          </Button>

          <Button
            variant="contained"
            color="primary"
            className={`primaryButton ${classes.button}`}
            onClick={() => redirect("booking")}
          >
            {language.Back_To_Booking}
          </Button>
        </div>
      )}

      {isSelf && (
        <div className={classes.info}>
          <Markdown
            source={text}
            escapeHtml={false}
            skipHtml={false}
            className="account__top-text"
          />
          <Divider className={classes.divider} />
        </div>
      )}
      <div className={classes.linkContainer}>
        {links &&
          links.map((l, i) => {
            const path = `${process.env.PUBLIC_URL}${l.path}`;
            return (
              <Link to={path} key={i} className={classes.link}>
                <div className={classes.card}>
                  <span className={classes.cardHeader}>
                    {language.Pages[l.name]}
                  </span>
                  <div className={classes.iconContainer}>
                    {getIcon(l.image, classes)}
                  </div>
                  <div className={classes.cardContent}>
                    <Typography className={classes.description}>
                      {language.Start_Description[l.name]}
                    </Typography>
                  </div>
                </div>
              </Link>
            );
          })}
      </div>
    </div>
  );
};

export default StartPage;
